import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { useCatalogSync } from '../home/hooks/useCatalogSync';
import { IndexingStatus } from '../home/operatorState';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  blocked: {
    pointerEvents: 'none',
    userSelect: 'none',
    opacity: 0.42,
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: theme.spacing(2.5, 3, 3),
    background: 'rgba(255, 255, 255, 0.76)',
    backdropFilter: 'blur(1px)',
  },
  panel: {
    maxWidth: 420,
    borderRadius: 8,
    border: '1px solid #d9e8f7',
    background: '#ffffff',
    boxShadow: '0 12px 30px rgba(27, 39, 51, 0.12)',
    padding: theme.spacing(2.5),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  progress: {
    flexShrink: 0,
    color: '#2f80ed',
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  details: {
    marginTop: theme.spacing(1),
    fontWeight: 600,
    color: '#174a7c',
  },
}));

function buildProgressLabel({ progress, progressKnown = true, isQueued, localizeMessage }) {
  if (isQueued) {
    return localizeMessage('operation.indexing.overlay.queuedProgress');
  }
  if (!progressKnown || typeof progress !== 'number') {
    return localizeMessage('operation.indexing.overlay.runningProgress');
  }
  if (progress >= 100) {
    return localizeMessage('operation.indexing.overlay.finalizing');
  }
  return localizeMessage('operation.indexing.overlay.progress', { progress });
}

export default function IndexingPageGuard({ children }) {
  const classes = useStyles();
  const localizeMessage = useLocalizedMessage();
  const catalogSync = useCatalogSync({ enabled: true });
  const isBlocked = catalogSync.status === IndexingStatus.RUNNING || catalogSync.isStarting;
  const isQueued = catalogSync.isStarting || catalogSync.rawStatus === 'queued';
  const progressLabel = isBlocked
    ? buildProgressLabel({
      progress: catalogSync.progress,
      progressKnown: catalogSync.progressKnown,
      isQueued,
      localizeMessage,
    })
    : null;
  const titleKey = isQueued
    ? 'operation.indexing.overlay.queuedTitle'
    : 'operation.indexing.overlay.title';
  const descriptionKey = isQueued
    ? 'operation.indexing.overlay.queuedDescription'
    : 'operation.indexing.overlay.description';

  return (
    <Box className={classes.root} data-testid="indexing-page-guard">
      <Box className={isBlocked ? classes.blocked : undefined}>
        {children}
      </Box>
      {isBlocked && (
        <Box className={classes.overlay} data-testid="indexing-page-guard-overlay">
          <Box className={classes.panel}>
            <CircularProgress className={classes.progress} size={34} />
            <Box>
              <Typography variant="subtitle1" className={classes.title}>
                {localizeMessage(titleKey)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {localizeMessage(descriptionKey)}
              </Typography>
              {progressLabel && (
                <Typography variant="body2" className={classes.details}>
                  {progressLabel}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

IndexingPageGuard.propTypes = {
  children: PropTypes.node,
};

IndexingPageGuard.defaultProps = {
  children: null,
};

export { buildProgressLabel };
